import { useCallback, useMemo } from 'react';

import {
  FiltersState,
  SearchFilters,
  StoredFilters,
} from '../../filters.types';
import { LocalStorageKeys, OccupancyType } from '@keaze/web/common/enums';
import { useLocalStorage } from '@keaze/web/hooks';
import { DEFAULT_FILTERS_STATE } from '../../filters';
import { useSearchQueryParams } from '../../../hooks';
import { DEFAULT_RADIUS_VALUES } from '../../helpers';
import { GetStoredFiltersByType } from './useFiltersFromStorage.types';

type UseFiltersFromStorageExpected = {
  storedFilters: StoredFilters;
  storedFiltersByType: FiltersState;
  updateStoredFilters: (filters: FiltersState) => void;
  getStoredFiltersByType: GetStoredFiltersByType;
};

type UseFiltersFromStorage = () => UseFiltersFromStorageExpected;

const generateStoredFiltersInitialData = () => {
  const { location, ...filters } = DEFAULT_FILTERS_STATE;

  return {
    [SearchFilters.Location]: '',
    [OccupancyType.Sale]: {
      ...filters,
      [SearchFilters.OccupancyType]: OccupancyType.Sale,
    },
    [OccupancyType.Rent]: {
      ...filters,
      [SearchFilters.OccupancyType]: OccupancyType.Rent,
    },
  };
};

export const useFiltersFromStorage: UseFiltersFromStorage = () => {
  const {
    type: occupancyTypeQueryParam,
    location: locationQueryParam,
    radius: radiusQueryParam,
    paramsForListing: { bedrooms: bedroomsQueryParam },
  } = useSearchQueryParams();
  const [storedFilters, setStoredFilters] = useLocalStorage<StoredFilters>(
    LocalStorageKeys.filters,
    generateStoredFiltersInitialData()
  );

  const updateStoredFilters = useCallback(
    (filters: FiltersState) => {
      const { location, ...restFilters } = filters;
      const occupancyType = filters[SearchFilters.OccupancyType] as string;

      setStoredFilters({
        ...storedFilters,
        [SearchFilters.Location]: location as string,
        [occupancyType]: restFilters,
      });
    },
    [storedFilters, setStoredFilters]
  );

  const getStoredFiltersByType: GetStoredFiltersByType = useCallback(
    (type) => {
      const occupancyType = type ?? OccupancyType.Sale;
      const storedFiltersByType = storedFilters[occupancyType];
      const storedLocation = storedFilters[SearchFilters.Location] as string;

      let filters: FiltersState = {
        ...storedFiltersByType,
        [SearchFilters.Location]: locationQueryParam ?? storedLocation,
      };

      // Support for the old filter storage without occupancy_type
      if (!storedFiltersByType[SearchFilters.OccupancyType]) {
        filters = {
          ...filters,
          [SearchFilters.OccupancyType]: occupancyType,
        };
      }

      if (radiusQueryParam) {
        const validRadius = DEFAULT_RADIUS_VALUES.indexOf(radiusQueryParam);

        if (validRadius !== -1) {
          filters = {
            ...filters,
            [SearchFilters.Radius]: radiusQueryParam,
          };
        }
      }

      if (bedroomsQueryParam) {
        filters = {
          ...filters,
          [SearchFilters.Bedrooms]: bedroomsQueryParam,
        };
      }

      return filters;
    },
    [storedFilters, locationQueryParam, radiusQueryParam, bedroomsQueryParam]
  );

  const storedFiltersByType = useMemo(
    () => getStoredFiltersByType(occupancyTypeQueryParam as OccupancyType),
    [occupancyTypeQueryParam, getStoredFiltersByType]
  );

  return {
    storedFilters,
    storedFiltersByType,
    updateStoredFilters,
    getStoredFiltersByType,
  };
};
